<script setup>
import { reactive, computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import {
  audienceListValue,
  toAudiencePayload,
} from 'dashboard/helper/campaignAudience';

const props = defineProps({
  presetAudienceListId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const DEFAULT_INTERVIEW_TIME = '10:00';
const INFOJOBS_MESSAGE_MAX_LENGTH = 4000;

const uiFlags = useMapGetter('campaigns/getUIFlags');
const audienceLists = useMapGetter('audienceLists/getAudienceLists');

const state = reactive({
  title: '',
  message: '',
  infojobsStoreId: '',
  pandapeVacancyId: '',
  selectedAudience: props.presetAudienceListId
    ? [audienceListValue(props.presetAudienceListId)]
    : [],
  channel: 'Mail',
  dateVariationEnabled: false,
  dateVariationDays: [],
  dateVariationTime: DEFAULT_INTERVIEW_TIME,
  sendAllDaily: false,
  dailySendLimit: '',
  scheduledAt: null,
});

const validDailySendLimit = value => {
  if (state.sendAllDaily) return true;

  const limit = Number(value);
  return Number.isInteger(limit) && limit > 0;
};

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  infojobsStoreId: { required },
  pandapeVacancyId: { required },
  selectedAudience: { required },
  channel: { required },
  dateVariationDays: {
    required: requiredIf(() => state.dateVariationEnabled),
  },
  dateVariationTime: {
    required: requiredIf(() => state.dateVariationEnabled),
  },
  dailySendLimit: { validDailySendLimit },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);
const isCreating = computed(() => uiFlags.value.isCreating);
const infojobsStores = ref([]);
const storesLoading = ref(true);
const storesLoadFailed = ref(false);
const pandapeVacancies = ref([]);
const vacanciesLoading = ref(true);
const vacanciesLoadFailed = ref(false);
const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const audienceOptions = computed(() =>
  audienceLists.value
    .filter(list => list.source_system === 'pandape_infojobs')
    .map(list => ({
      value: audienceListValue(list.id),
      label: list.name,
    }))
);

const channelOptions = computed(() => [
  { value: 'Mail', label: t('CAMPAIGN.INFOJOBS.CREATE.FORM.CHANNELS.MAIL') },
  { value: 'SMS', label: t('CAMPAIGN.INFOJOBS.CREATE.FORM.CHANNELS.SMS') },
  {
    value: 'WhatsApp',
    label: t('CAMPAIGN.INFOJOBS.CREATE.FORM.CHANNELS.WHATSAPP'),
  },
]);

const storeOptions = computed(() =>
  infojobsStores.value.map(store => ({
    value: store.id,
    label: `${store.name} — ${store.address}`,
  }))
);

const selectedStore = computed(() =>
  infojobsStores.value.find(store => store.id === state.infojobsStoreId)
);

const vacancyOptions = computed(() =>
  pandapeVacancies.value.map(vacancy => ({
    value: vacancy.id,
    label: `${vacancy.title} — ${vacancy.location} (#${vacancy.reference})`,
  }))
);

const selectedVacancy = computed(() =>
  pandapeVacancies.value.find(vacancy => vacancy.id === state.pandapeVacancyId)
);

onMounted(async () => {
  const [storesResult, vacanciesResult] = await Promise.allSettled([
    CampaignsAPI.infojobsStores(),
    CampaignsAPI.pandapeVacancies(),
  ]);

  if (storesResult.status === 'fulfilled') {
    infojobsStores.value = storesResult.value.data.payload || [];
  } else {
    storesLoadFailed.value = true;
  }
  storesLoading.value = false;

  if (vacanciesResult.status === 'fulfilled') {
    pandapeVacancies.value = vacanciesResult.value.data.payload || [];
  } else {
    vacanciesLoadFailed.value = true;
  }
  vacanciesLoading.value = false;
});

const dateVariationDayOptions = computed(() => [
  {
    value: 1,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.MONDAY'),
  },
  {
    value: 2,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.TUESDAY'),
  },
  {
    value: 3,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.WEDNESDAY'),
  },
  {
    value: 4,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.THURSDAY'),
  },
  {
    value: 5,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.FRIDAY'),
  },
  {
    value: 6,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.SATURDAY'),
  },
  {
    value: 0,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.SUNDAY'),
  },
]);

const dateVariationTokens = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.DATE'),
    value: '{{ interview.proxima_data }}',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.TIME'),
    value: '{{ interview.proxima_horario }}',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.DATETIME'),
    value: '{{ interview.proxima_data_hora_extenso }}',
  },
]);

const variables = [
  'first_name',
  'full_name',
  'email',
  'city',
  'state',
  'desired_role',
];

const appendToken = token => {
  state.message = `${state.message}${state.message ? ' ' : ''}${token}`;
};

const appendVariable = variable => appendToken(`{{ candidate.${variable} }}`);

const browserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const parseDateVariationTime = () => {
  const match = state.dateVariationTime?.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;

  return { hour: Number(match[1]), minute: Number(match[2]) };
};

const dateVariationRules = () => {
  if (!state.dateVariationEnabled) return {};

  return {
    enabled: true,
    days_of_week: [...new Set(state.dateVariationDays.map(Number))].sort(
      (left, right) => left - right
    ),
    time: state.dateVariationTime,
    timezone: browserTimezone(),
  };
};

const formatDateVariationTime = date => {
  const hour = date.getHours();
  const minute = date.getMinutes();
  return minute === 0
    ? `${hour}h`
    : `${hour}h${String(minute).padStart(2, '0')}`;
};

const dateVariationPeriod = hour => {
  if (hour < 6) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.DAWN');
  }
  if (hour < 12) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.MORNING');
  }
  if (hour < 18) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.AFTERNOON');
  }

  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.NIGHT');
};

const nextDateVariationOccurrence = computed(() => {
  if (!state.dateVariationEnabled || state.dateVariationDays.length === 0) {
    return null;
  }

  const time = parseDateVariationTime();
  if (!time) return null;

  const now = new Date();
  const selectedDays = state.dateVariationDays.map(Number);
  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + offset);
    candidate.setHours(time.hour, time.minute, 0, 0);
    if (selectedDays.includes(candidate.getDay()) && candidate >= now) {
      return candidate;
    }
  }

  return null;
});

const nextDateVariationPreview = computed(() => {
  const occurrence = nextDateVariationOccurrence.value;
  if (!occurrence) return '';

  const date = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(occurrence);
  const time = formatDateVariationTime(occurrence);

  return `${date} - ${time} ${dateVariationPeriod(occurrence.getHours())}`;
});

const toggleDateVariationDay = dayValue => {
  state.dateVariationDays = state.dateVariationDays.includes(dayValue)
    ? state.dateVariationDays.filter(value => value !== dayValue)
    : [...state.dateVariationDays, dayValue];
};

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.TITLE.ERROR')
    : '',
  message: v$.value.message.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.MESSAGE.ERROR')
    : '',
  infojobsStoreId: v$.value.infojobsStoreId.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.STORE.ERROR')
    : '',
  pandapeVacancyId: v$.value.pandapeVacancyId.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.VACANCY.ERROR')
    : '',
  audience: v$.value.selectedAudience.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.AUDIENCE.ERROR')
    : '',
  scheduledAt: v$.value.scheduledAt.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.SCHEDULED_AT.ERROR')
    : '',
  dailySendLimit: v$.value.dailySendLimit.$error
    ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.DAILY_LIMIT.ERROR')
    : '',
  dateVariationDays: v$.value.dateVariationDays.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.ERROR')
    : '',
  dateVariationTime: v$.value.dateVariationTime.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TIME.ERROR')
    : '',
}));

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const handleSubmit = async () => {
  if (!(await v$.value.$validate())) return;

  emit('submit', {
    title: state.title,
    message: state.message,
    infojobs_store_id: state.infojobsStoreId,
    pandape_vacancy_id: Number(state.pandapeVacancyId),
    delivery_channel: 'infojobs',
    inbox_id: null,
    scheduled_at: formatToUTCString(state.scheduledAt),
    daily_send_limit: state.sendAllDaily ? null : Number(state.dailySendLimit),
    date_variation_rules: dateVariationRules(),
    audience: toAudiencePayload(state.selectedAudience),
    template_params: {
      infojobs_channels: [state.channel],
      delivery_controls: { daily_send_timezone: browserTimezone() },
    },
  });
  emit('cancel');
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="v$.title.$error ? 'error' : 'info'"
    />
    <div class="flex flex-col gap-1">
      <label
        for="pandape-vacancy"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.VACANCY.LABEL') }}
      </label>
      <ComboBox
        id="pandape-vacancy"
        v-model="state.pandapeVacancyId"
        :options="vacancyOptions"
        :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.VACANCY.PLACEHOLDER')"
        :disabled="vacanciesLoading"
        :has-error="v$.pandapeVacancyId.$error || vacanciesLoadFailed"
        :message="
          vacanciesLoadFailed
            ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.VACANCY.LOAD_ERROR')
            : formErrors.pandapeVacancyId
        "
      />
      <p v-if="selectedVacancy" class="text-xs text-n-slate-11">
        {{ selectedVacancy.location }} · #{{ selectedVacancy.reference }}
      </p>
    </div>
    <div class="flex flex-col gap-1">
      <label
        for="infojobs-store"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.STORE.LABEL') }}
      </label>
      <ComboBox
        id="infojobs-store"
        v-model="state.infojobsStoreId"
        :options="storeOptions"
        :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.STORE.PLACEHOLDER')"
        :disabled="storesLoading"
        :has-error="v$.infojobsStoreId.$error || storesLoadFailed"
        :message="
          storesLoadFailed
            ? t('CAMPAIGN.INFOJOBS.CREATE.FORM.STORE.LOAD_ERROR')
            : formErrors.infojobsStoreId
        "
      />
      <p v-if="selectedStore" class="text-xs text-n-slate-11">
        {{ selectedStore.address }}
      </p>
    </div>
    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :max-length="INFOJOBS_MESSAGE_MAX_LENGTH"
      show-character-count
      :message="formErrors.message"
      :message-type="v$.message.$error ? 'error' : 'info'"
    />
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.VARIABLES.LABEL') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <Button
          v-for="variable in variables"
          :key="variable"
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          :label="`{{ candidate.${variable} }}`"
          @click="appendVariable(variable)"
        />
        <Button
          v-if="selectedStore"
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          :label="'{{ store.address }}'"
          :title="t('CAMPAIGN.INFOJOBS.CREATE.FORM.STORE.VARIABLE')"
          @click="appendToken('{{ store.address }}')"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.VARIABLES.INFO') }}
      </p>
    </div>
    <div
      class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.INFO') }}
        </p>
      </div>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input
          v-model="state.dateVariationEnabled"
          type="checkbox"
          class="size-4 accent-n-blue-9"
        />
        <span>
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.ENABLED') }}
        </span>
      </label>
      <template v-if="state.dateVariationEnabled">
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.LABEL') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="day in dateVariationDayOptions"
              :key="day.value"
              type="button"
              class="px-3 py-1.5 text-sm border rounded-full transition-colors"
              :class="
                state.dateVariationDays.includes(day.value)
                  ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="toggleDateVariationDay(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
          <p v-if="formErrors.dateVariationDays" class="text-xs text-n-ruby-11">
            {{ formErrors.dateVariationDays }}
          </p>
        </div>
        <Input
          v-model="state.dateVariationTime"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TIME.LABEL')"
          type="time"
          :message="formErrors.dateVariationTime"
          :message-type="formErrors.dateVariationTime ? 'error' : 'info'"
        />
        <p v-if="nextDateVariationPreview" class="text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PREVIEW', {
              value: nextDateVariationPreview,
            })
          }}
        </p>
        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.LABEL') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button
              v-for="token in dateVariationTokens"
              :key="token.value"
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              :label="token.value"
              :title="token.label"
              @click="appendToken(token.value)"
            />
          </div>
        </div>
      </template>
    </div>
    <div class="flex flex-col gap-1">
      <label
        for="infojobs-audience"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        id="infojobs-audience"
        v-model="state.selectedAudience"
        :options="audienceOptions"
        :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="v$.selectedAudience.$error"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label
        for="infojobs-channels"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.CHANNELS.LABEL') }}
      </label>
      <ComboBox
        id="infojobs-channels"
        v-model="state.channel"
        :options="channelOptions"
        :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.CHANNELS.PLACEHOLDER')"
      />
    </div>
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :message="formErrors.scheduledAt"
      :message-type="v$.scheduledAt.$error ? 'error' : 'info'"
    />
    <div
      class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input
          v-model="state.sendAllDaily"
          type="checkbox"
          class="size-4 accent-n-blue-9"
        />
        <span>{{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.SEND_ALL.LABEL') }}</span>
      </label>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.INFOJOBS.CREATE.FORM.SEND_ALL.INFO') }}
      </p>
      <Input
        v-if="!state.sendAllDaily"
        v-model="state.dailySendLimit"
        :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.DAILY_LIMIT.LABEL')"
        :placeholder="t('CAMPAIGN.INFOJOBS.CREATE.FORM.DAILY_LIMIT.PLACEHOLDER')"
        type="number"
        min="1"
        step="1"
        :message="
          formErrors.dailySendLimit ||
          t('CAMPAIGN.INFOJOBS.CREATE.FORM.DAILY_LIMIT.INFO')
        "
        :message-type="formErrors.dailySendLimit ? 'error' : 'info'"
      />
    </div>
    <div
      class="flex flex-col-reverse w-full gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="emit('cancel')"
      />
      <Button
        :label="t('CAMPAIGN.INFOJOBS.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="
          isCreating ||
          storesLoading ||
          storesLoadFailed ||
          vacanciesLoading ||
          vacanciesLoadFailed ||
          v$.$invalid
        "
      />
    </div>
  </form>
</template>
